import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { EASE, Magnetic } from "./Reveal";
import { AUDIENCE_LINKS, CONTACT, NAV_LINKS } from "../data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const { pathname } = useLocation();
  const darkHero = pathname === "/" || AUDIENCE_LINKS.some((a) => a.to === pathname);
  const solid = scrolled || open;
  const light = solid || !darkHero;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setDropOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (open) {
      window.__lenis?.stop();
    } else {
      window.__lenis?.start();
    }
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [open]);

  const linkCls = (isActive) =>
    `link-underline text-sm font-semibold tracking-wide transition-colors duration-300 ${
      isActive
        ? light
          ? "text-golddark"
          : "text-herald"
        : light
          ? "text-ink/70 hover:text-ink"
          : "text-white/75 hover:text-white"
    }`;

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 ${
          solid
            ? "bg-surface/85 backdrop-blur-xl border-b border-line/70"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="container-x flex items-center justify-between h-[76px] md:h-[88px]">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img
              src={light ? "/images/logo-komposition.png" : "/images/logo-komposition-white.png"}
              alt="Manfred Berglar – Malermeister seit 1925"
              className="h-14 md:h-16 w-auto"
            />
          </Link>
          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.slice(0, 2).map((l) => (
              <NavLink key={l.id} to={l.to} end={l.to === "/"} data-testid={`nav-link-${l.id}`} className={({ isActive }) => linkCls(isActive)}>
                {l.label}
              </NavLink>
            ))}
            <div
              className="relative"
              onMouseEnter={() => setDropOpen(true)}
              onMouseLeave={() => setDropOpen(false)}
            >
              <button
                data-testid="nav-audiences-toggle"
                aria-expanded={dropOpen}
                className={`flex items-center gap-1.5 text-sm font-semibold tracking-wide transition-colors duration-300 ${
                  AUDIENCE_LINKS.some((a) => a.to === pathname)
                    ? light
                      ? "text-golddark"
                      : "text-herald"
                    : light
                      ? "text-ink/70 hover:text-ink"
                      : "text-white/75 hover:text-white"
                }`}
              >
                Zielgruppen <ChevronDown size={14} className={`transition-transform duration-300 ${dropOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {dropOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.25, ease: EASE }}
                    className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
                    data-testid="nav-audiences-menu"
                  >
                    <div className="bg-white border border-line rounded-xl shadow-[0_20px_44px_-18px_rgba(21,21,21,0.3)] min-w-[240px] p-2">
                      {AUDIENCE_LINKS.map((a) => (
                        <NavLink
                          key={a.id}
                          to={a.to}
                          data-testid={`nav-link-${a.id}`}
                          className={({ isActive }) =>
                            `flex items-center justify-between px-4 py-3 rounded-lg text-sm font-semibold transition-colors duration-300 ${
                              isActive ? "text-golddark bg-surface" : "text-ink/70 hover:text-golddark hover:bg-surface"
                            }`
                          }
                        >
                          {a.label} <ArrowUpRight size={13} />
                        </NavLink>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {NAV_LINKS.slice(2).map((l) => (
              <NavLink key={l.id} to={l.to} data-testid={`nav-link-${l.id}`} className={({ isActive }) => linkCls(isActive)}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Magnetic strength={14} className="hidden sm:inline-block">
              <Link to="/kontakt" data-testid="nav-cta-button" className={`${light ? "btn-primary" : "btn-light"} !py-3 !px-6`}>
                Projekt anfragen <ArrowUpRight size={15} />
              </Link>
            </Magnetic>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((o) => !o)}
              className={`lg:hidden w-11 h-11 flex items-center justify-center transition-colors duration-500 ${light ? "text-ink" : "text-white"}`}
              aria-label="Menü öffnen"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[55] bg-ink/50 backdrop-blur-sm lg:hidden"
              data-testid="mobile-menu-backdrop"
            />
            <motion.nav
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: EASE }}
              className="fixed inset-y-0 right-0 z-[60] w-[86%] max-w-sm bg-navy bg-grain border-l border-white/10 flex flex-col lg:hidden"
              data-testid="mobile-menu"
            >
              <div className="flex items-center justify-between px-6 h-[76px] border-b border-white/10 relative">
                <img src="/images/logo-komposition-white.png" alt="Manfred Berglar – Malermeister seit 1925" className="h-12 w-auto" />
                <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Menü schließen" className="w-11 h-11 flex items-center justify-center text-white">
                  <X size={22} />
                </button>
              </div>
              <div className="flex flex-col px-6 pt-6 overflow-y-auto relative">
                {[...NAV_LINKS].map((l, i) => (
                  <motion.div key={l.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 + i * 0.05, duration: 0.4, ease: EASE }}>
                    <NavLink
                      to={l.to}
                      end={l.to === "/"}
                      data-testid={`mobile-nav-link-${l.id}`}
                      className={({ isActive }) =>
                        `block py-4 font-display text-2xl font-semibold border-b border-white/10 transition-colors duration-300 ${
                          isActive ? "text-herald" : "text-white"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                ))}
                <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-herald mt-6 mb-1">Zielgruppen</p>
                {AUDIENCE_LINKS.map((l) => (
                  <NavLink
                    key={l.id}
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className={({ isActive }) =>
                      `block py-3.5 font-display text-xl font-medium border-b border-white/10 transition-colors duration-300 ${
                        isActive ? "text-herald" : "text-white/80"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
              <div className="mt-auto px-6 pb-8 pt-6 relative">
                <Link to="/kontakt" data-testid="mobile-nav-cta" className="btn-light w-full">
                  Projekt anfragen <ArrowUpRight size={15} />
                </Link>
                <a href={CONTACT.phoneHref} data-testid="mobile-nav-phone" className="block text-center font-mono text-xs tracking-widest text-white/60 mt-5">
                  {CONTACT.phoneDisplay}
                </a>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
